import { brandName, brandLogo } from '../lib/brandTheme.js';
import { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { loadFaceDetector, classifyFace } from '../lib/faceGuide.js';
import { isSpeechFollowSupported, buildScriptWords, advancePointer, promptIdxForPointer } from '../lib/speechFollow.js';

// ScriptRecorder — handoff v3 rewrite
// ──────────────────────────────────────────────────────────────────────
// Same two-column layout as before (camera stage left, side panel right),
// but the surface is far richer:
//
//   permission — pre-flight card explaining why we need cam+mic, with
//                a single big "Allow camera & microphone" button. Shows
//                an error helper if the browser denies access. Replaces
//                the old "tap to turn on camera" intro.
//
//   setup      — camera live, side panel shows the start pill ("Tap to
//                start recording / 5-second countdown, then read aloud"),
//                three setup tips (lighting / quiet room / re-record),
//                and optionally a network-slow warning. Real-time audio
//                meter bars below the stage so the customer sees the mic
//                is working before they record.
//
//   countdown  — 5 second prep (was 3) with per-second tips in the
//                stage overlay ("Take a breath" → "Sit up straight" →
//                "Look at the camera" → "Smile if you feel like it" →
//                "Here we go").
//
//   recording  — MediaRecorder capturing; audio bars continue;
//                encouragement messages fade in at 6s/14s/22s ("Doing
//                great. Just keep reading.", etc); silence detection
//                auto-stops the take after 2.5s of quiet (only after
//                5s of recording elapsed, so a thinking pause doesn't
//                trigger it); 3-2-1 toast as we approach the auto-stop.
//
//   uploading  — Stream's two-step model surfaced as two visible stages:
//                  1. Sending      (real % progress from XHR onprogress)
//                  2. Processing   (real /accept call, ends when it
//                                   resolves and the row is in DB)
//                Then 'ready' (visible briefly) → phase flips to review
//                automatically. Wait-tip below the bar.
//
//   review     — preview the take. Friendly "How did that feel?" prompt
//                above the action row. Redo / Keep this one buttons.
//
//   error      — friendly error message + retry CTA.
//
// Live preview is mirrored (selfie-style); recorded blob is NOT mirrored
// so the marketing-ready video has correct orientation. Audio bars use
// a real AnalyserNode hooked to the mic stream — they reflect actual
// volume so the customer can see "yes, you can hear me" before they
// record (a real reassurance step for boomers).
//
// All upstream interactions are unchanged from the prior version:
//   POST /api/clip/upload-url    → { clip_id, upload_url }  (Stream direct-upload)
//   XHR multipart POST upload_url → puts the webm to Stream
//   POST /api/clip/:id/accept    → finalizes, returns the clip row
//
// State machine:
//   permission → setup → countdown → recording → uploading → review
//                                    ↑                       ↓
//                                    └──────── redo ─────────┘
//   any → error → permission (retry)

const PREFERRED_MIMES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=h264,aac',
  'video/mp4',
];

function pickMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

const TELE_SIZES = { sm: 22, md: 30, lg: 38 };
const TELE_PREF_KEY = 'vt-tele-size';

// Silence detection thresholds. RMS < 0.04 for HOLD_MS triggers the
// auto-stop, but only if at least MIN_RECORDED_MS has elapsed (so a
// thoughtful pause at the start doesn't kill the take). The 3-2-1
// warning toast surfaces during the last 3 seconds of the hold so the
// customer has a chance to recover.
const SILENCE_RMS_THRESHOLD = 0.04;
const SILENCE_HOLD_MS       = 2500;
const SILENCE_MIN_ELAPSED   = 5;     // seconds of recording before auto-stop is allowed

// Encouragement messages — keyed by elapsed seconds during recording.
// Picked to feel like a friend nodding from off-camera, not a
// drill-sergeant. Each shows for 2.8s then fades.
const ENCOURAGEMENT_BY_SECOND = {
  6:  'Doing great. Just keep reading.',
  14: 'You sound natural. Nice work.',
  22: 'Almost there. Stay with it.',
};

// Per-second tip text during the 5-second countdown. count is 5..1, then
// 'Go!' at 0. Boomer-friendly: each tells them one tiny thing to do.
const COUNTDOWN_TIPS = {
  5: 'Take a breath.',
  4: 'Sit up straight.',
  3: 'Look at the camera.',
  2: 'Smile if you feel like it.',
  1: 'Here we go.',
};

export default function ScriptRecorder({
  script,
  invite_token,
  maxDurationSeconds = 60,
  filledScript,
  onAccepted,
  onCancel,
  clipNumber = 1,
  totalClips = 3,
  brand = null,
  practice = false,
  onSkip = null,
  freeform = false,
  autoUpload = false,
  interview = false,
  getUploadUrl = null,   // custom upload target: async () => ({ upload_url }); skips clip creation + accept
  questionId = null,
  questionText = null,
  questionHint = null,
}) {
  // ── Phase + permission state ────────────────────────────────────────
  // Phase machine — see top-of-file diagram for state transitions.
  const [phase, setPhase] = useState('permission');
  const [permState, setPermState] = useState('idle'); // idle | asking | denied
  const [error, setError] = useState(null);

  // ── Recording state ────────────────────────────────────────────────
  const [countdown, setCountdown] = useState(5);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [takeNumber, setTakeNumber] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);

  // ── Upload state (two visible stages: sending vs processing) ──────
  // sending  — XHR PUT in flight, uploadPct is real progress.
  // processing — XHR completed, /accept call in flight.
  // ready    — /accept returned, brief 'all done' beat before flipping
  //            to review phase.
  const [uploadStage, setUploadStage] = useState('sending');
  const [uploadPct, setUploadPct] = useState(0);

  // ── Audio meter + silence detection ───────────────────────────────
  // bars are 32 floats 0..1 driven by real FFT data. silentMs counts
  // up while RMS is below threshold during recording; resets when we
  // hear them again. autoStopIn is null normally; counts down 3/2/1
  // as we approach the silence cutoff so the customer sees it coming.
  const [bars, setBars] = useState(() => new Array(32).fill(0.1));
  // Adaptive head-guide: 'none' (no face / off) | 'adjust' | 'good'. null = the
  // detector hasn't loaded yet, so we show the neutral static guide.
  const [faceState, setFaceState] = useState(null);
  const [faceHint, setFaceHint] = useState('');
  const [autoStopIn, setAutoStopIn] = useState(null);

  // ── Ambient state ─────────────────────────────────────────────────
  const [encouragement, setEncouragement] = useState(null);
  const [networkSlow, setNetworkSlow] = useState(false);
  const [teleSize, setTeleSize] = useState(() => {
    try { return localStorage.getItem(TELE_PREF_KEY) || 'lg'; } catch { return 'lg'; }
  });

  // ── Refs ──────────────────────────────────────────────────────────
  const liveVideoRef    = useRef(null);
  const stageRef        = useRef(null);
  const faceDetectorRef = useRef(null);
  const faceRafRef      = useRef(null);
  const reviewVideoRef  = useRef(null);
  const mediaStreamRef  = useRef(null);
  const recorderRef     = useRef(null);
  const chunksRef       = useRef([]);
  const blobRef         = useRef(null);
  const recordTimerRef  = useRef(null);
  const startedAtRef    = useRef(null);

  // Audio analysis refs — one AudioContext + AnalyserNode lives for the
  // whole component once we have the stream. The rAF loop reads from
  // this analyser and updates bars + silence counters.
  const audioCtxRef     = useRef(null);
  const analyserRef     = useRef(null);
  const sourceNodeRef   = useRef(null);
  const rafRef          = useRef(null);
  const silenceMsRef    = useRef(0);     // mutable counter, avoids re-renders on every rAF tick
  const maxAudioRef     = useRef(0);     // running peak audio level during the take (practice eval)
  // Practice readiness gate state: 'idle' | 'checking' | 'ready' | 'retry'
  const [practiceCheck, setPracticeCheck] = useState('idle');
  const [evalResult, setEvalResult] = useState(null);  // full per-dimension result for the breakdown screen
  const [practiceTip, setPracticeTip] = useState('');
  const practiceAttemptsRef = useRef(0);
  // Upload-a-video-instead (non-practice only).
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const autoUploadFiredRef = useRef(false);
  const isUploadRef  = useRef(false);

  // "Upload a video" entry (free-form): jump straight to the file chooser once
  // the camera setup screen is up, so the customer doesn't have to hunt for it.
  useEffect(() => {
    if (freeform && autoUpload && phase === 'setup' && !autoUploadFiredRef.current) {
      autoUploadFiredRef.current = true;
      setTimeout(() => fileInputRef.current && fileInputRef.current.click(), 350);
    }
  }, [freeform, autoUpload, phase]);
  const acceptTakeRef = useRef(null);  // latest acceptTake, so the eval gate can call it without stale closures
  // Speech-follow: when supported (and not iOS), the teleprompter advances to
  // what they're actually saying. null = use the time-based scroll instead.
  const [spokenPromptIdx, setSpokenPromptIdx] = useState(null);
  const srRef          = useRef(null);
  const srWantRef      = useRef(false);
  const srWordsRef     = useRef([]);
  const srPointerRef   = useRef(0);
  const srTranscriptRef= useRef('');

  useEffect(() => {
    try { localStorage.setItem(TELE_PREF_KEY, teleSize); } catch { /* ignore */ }
  }, [teleSize]);

  // ── Cleanup on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { audioCtxRef.current?.close(); } catch { /* ignore */ }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Permission preflight ──────────────────────────────────────────
  // Async getUserMedia with a friendly fallback constraint set. On
  // success: hold the stream in mediaStreamRef and advance to 'setup'.
  // Also: probe navigator.connection.effectiveType for slow-network
  // warning copy on the setup screen.
  const requestPermission = useCallback(async () => {
    setError(null);
    setPermState('asking');

    // Detect slow connections — shown as a warning during setup.
    // The API isn't universal (Firefox+Safari miss it) but where it
    // exists it's useful.
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const et = conn?.effectiveType;
    const slow = et === '2g' || et === 'slow-2g' || (conn?.downlink && conn.downlink < 1);
    setNetworkSlow(!!slow);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('This browser cannot use the camera. Please open the link in Safari, Chrome, or Edge.');
      setPermState('denied');
      return;
    }

    const tryGet = (constraints) => navigator.mediaDevices.getUserMedia(constraints);
    let stream;
    try {
      stream = await tryGet({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
    } catch (e1) {
      console.warn('getUserMedia first attempt failed', e1?.name, e1?.message);
      const retryable = ['OverconstrainedError', 'ConstraintNotSatisfiedError', 'NotReadableError', 'AbortError'];
      if (retryable.includes(e1?.name)) {
        try { stream = await tryGet({ video: true, audio: true }); }
        catch (e2) { return failWith(e2); }
      } else {
        return failWith(e1);
      }
    }

    mediaStreamRef.current = stream;

    // Wire up the analyser node on the same stream — drives the bars
    // and the silence counter. AudioContext sometimes needs a user
    // gesture to start; this function IS a user gesture (clicked the
    // Allow button) so the resume() call below is safe.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctx = new AC();
        if (ctx.state === 'suspended') await ctx.resume();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;          // 128 freq bins; downsampled to 32 bars
        analyser.smoothingTimeConstant = 0.6;
        source.connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        sourceNodeRef.current = source;
      }
    } catch (e) {
      // Audio meter is a nice-to-have; if it fails, recording still works.
      console.warn('AudioContext setup failed', e?.name, e?.message);
    }

    setPermState('idle');
    setPhase('setup');

    function failWith(e) {
      console.error('getUserMedia failed', e?.name, e?.message);
      const name = e?.name || 'Error';
      const friendly = {
        NotAllowedError:               'Camera access was blocked. Open your browser settings, allow camera and microphone for this page, then tap Try again.',
        PermissionDeniedError:         'Camera access was blocked. Open your browser settings, allow camera and microphone for this page, then tap Try again.',
        NotFoundError:                 'No camera was found on this device.',
        DevicesNotFoundError:          'No camera was found on this device.',
        NotReadableError:              'Your camera is already being used by another app. Close it (FaceTime, Zoom, etc.) and tap Try again.',
        TrackStartError:               'Your camera is already being used by another app. Close it and tap Try again.',
        OverconstrainedError:          'Your camera does not support recording in this browser.',
        ConstraintNotSatisfiedError:   'Your camera does not support recording in this browser.',
        SecurityError:                 'This page must be loaded over a secure (https) connection to use the camera.',
        AbortError:                    'Camera startup was interrupted. Tap Try again.',
      }[name] || `We could not access your camera. (${name})`;
      setError(friendly);
      setPermState('denied');
    }
  }, []);

  // ── Attach live stream to <video> for phases that show preview ────
  useEffect(() => {
    const v = liveVideoRef.current;
    const stream = mediaStreamRef.current;
    const phaseShowsLive = phase === 'setup' || phase === 'countdown' || phase === 'recording' || phase === 'uploading';
    if (!v || !stream || !phaseShowsLive) return;
    if (v.srcObject === stream) return;

    v.srcObject = stream;
    v.muted = true;           // no feedback loop on the preview
    v.playsInline = true;
    // Match the stage box to the real video aspect ratio so the preview fills
    // edge-to-edge (no letterbox) and the head-position guide lines up with the
    // actual frame instead of floating over black bars.
    const fitStage = () => {
      const st = stageRef.current;
      if (st && v.videoWidth && v.videoHeight) {
        st.style.aspectRatio = `${v.videoWidth} / ${v.videoHeight}`;
      }
    };
    v.onloadedmetadata = fitStage;
    if (v.videoWidth) fitStage();
    const playPromise = v.play();
    if (playPromise && playPromise.catch) {
      playPromise.catch((err) => console.warn('video.play() rejected', err?.name, err?.message));
    }
  }, [phase]);

  // ── Adaptive head-guide: face detection loop ──────────────────────
  // Best-effort. Loads MediaPipe lazily; if it never loads, faceState stays
  // null and the neutral static guide shows. Runs ~5fps while the camera is
  // visible (setup/countdown/recording) to keep CPU/battery light.
  useEffect(() => {
    const guideActive = phase === 'setup' || phase === 'countdown' || phase === 'recording';
    if (!guideActive) {
      if (faceRafRef.current) { cancelAnimationFrame(faceRafRef.current); faceRafRef.current = null; }
      return;
    }
    let cancelled = false;
    // Oval target as fractions of the frame — matches the CSS guide
    // (height ~52%, nudged up 6%). Face height target ~0.42 of frame.
    const TARGET = { cx: 0.5, cy: 0.42, h: 0.30 };  // head-and-shoulders: smaller face = further from camera
    const DETECT_EVERY_MS = 200;
    let lastDetect = 0;

    (async () => {
      if (!faceDetectorRef.current) {
        faceDetectorRef.current = await loadFaceDetector(); // null on failure
      }
      const det = faceDetectorRef.current;
      if (!det || cancelled) return; // no detector → static guide stays

      const loop = () => {
        if (cancelled) return;
        const v = liveVideoRef.current;
        const now = performance.now();
        if (v && v.readyState >= 2 && v.videoWidth && now - lastDetect >= DETECT_EVERY_MS) {
          lastDetect = now;
          try {
            const res = det.detectForVideo(v, now);
            const { state, hint } = classifyFace(res, v.videoWidth, v.videoHeight, TARGET);
            setFaceState(state);
            setFaceHint(hint);
          } catch { /* transient frame error — ignore */ }
        }
        faceRafRef.current = requestAnimationFrame(loop);
      };
      faceRafRef.current = requestAnimationFrame(loop);
    })();

    return () => {
      cancelled = true;
      if (faceRafRef.current) { cancelAnimationFrame(faceRafRef.current); faceRafRef.current = null; }
    };
  }, [phase]);

  // ── Speech-follow: scroll the teleprompter to what they're saying ──
  // Best-effort, recording-only. Falls back silently to the time-based scroll
  // when unsupported (incl. iOS), on error, or if nothing is heard early on.
  // NEVER touches the recorded audio — SpeechRecognition runs its own session.
  useEffect(() => {
    if (phase !== 'recording') {
      srWantRef.current = false;
      if (srRef.current) { try { srRef.current.stop(); } catch { /* */ } srRef.current = null; }
      setSpokenPromptIdx(null);
      return;
    }
    if (!isSpeechFollowSupported()) return;  // time-based scroll stays

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    srWordsRef.current = buildScriptWords(script, filledScript);
    srPointerRef.current = 0;
    srTranscriptRef.current = '';
    srWantRef.current = true;
    let gotAnything = false;

    let rec;
    try {
      rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;
    } catch { return; }

    rec.onresult = (e) => {
      gotAnything = true;
      // Rebuild the cumulative transcript from all results (final + interim).
      let text = '';
      for (let i = 0; i < e.results.length; i++) text += e.results[i][0].transcript + ' ';
      srTranscriptRef.current = text;
      const next = advancePointer(srWordsRef.current, text, srPointerRef.current);
      if (next !== srPointerRef.current) {
        srPointerRef.current = next;
        setSpokenPromptIdx(promptIdxForPointer(srWordsRef.current, next));
      }
    };
    rec.onerror = (ev) => {
      // not-allowed / service-not-allowed → give up, fall back to time-based.
      if (ev?.error === 'not-allowed' || ev?.error === 'service-not-allowed') {
        srWantRef.current = false;
        setSpokenPromptIdx(null);
      }
    };
    rec.onend = () => {
      // Chrome ends recognition periodically; restart while still recording.
      if (srWantRef.current) { try { rec.start(); } catch { /* */ } }
    };

    try { rec.start(); srRef.current = rec; } catch { srWantRef.current = false; }

    // Watchdog: if we never heard anything in the first few seconds, the engine
    // probably isn't working here — stop trying and let time-based take over.
    const watchdog = setTimeout(() => {
      if (!gotAnything) { srWantRef.current = false; try { rec.stop(); } catch { /* */ } setSpokenPromptIdx(null); }
    }, 5000);

    return () => {
      clearTimeout(watchdog);
      srWantRef.current = false;
      try { rec.stop(); } catch { /* */ }
      srRef.current = null;
    };
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Audio meter rAF loop ──────────────────────────────────────────
  // Active during setup + recording. Reads real FFT data, downsamples
  // to 32 bars, computes peak as a proxy for "are they making sound."
  // Silence detection runs on top of the same loop during recording.
  useEffect(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const meterActive = phase === 'setup' || phase === 'recording';
    if (!meterActive) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const buf = new Uint8Array(analyser.frequencyBinCount);
    let last = performance.now();
    silenceMsRef.current = 0;

    const tick = () => {
      const now = performance.now();
      const dt = now - last;
      last = now;

      analyser.getByteFrequencyData(buf);

      // Downsample 128 bins to 32 bars by averaging chunks of 4.
      // Also compute peak across all bins, used for silence detection.
      const next = new Array(32);
      let peak = 0;
      const step = buf.length / 32;
      for (let i = 0; i < 32; i++) {
        let sum = 0;
        const start = Math.floor(i * step);
        const end   = Math.floor((i + 1) * step);
        for (let j = start; j < end; j++) sum += buf[j];
        const avg = sum / (end - start) / 255;   // → 0..1
        next[i] = avg;
        if (avg > peak) peak = avg;
      }
      setBars(next);

      // Track the loudest moment of the take, used by the practice gate to
      // tell "we heard you" from "silent room."
      if (phase === 'recording' && peak > maxAudioRef.current) maxAudioRef.current = peak;

      // Silence detection — only counts during the recording phase.
      if (phase === 'recording') {
        if (peak < SILENCE_RMS_THRESHOLD) {
          silenceMsRef.current += dt;
          // Show the 3-2-1 warning when we're inside the last 3000ms of the hold
          if (silenceMsRef.current > SILENCE_HOLD_MS - 3000) {
            const remaining = Math.max(0, Math.ceil((SILENCE_HOLD_MS - silenceMsRef.current) / 1000));
            setAutoStopIn(remaining || 1);
          }
          const recordedSeconds = startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0;
          if (silenceMsRef.current >= SILENCE_HOLD_MS && recordedSeconds > SILENCE_MIN_ELAPSED) {
            stopRecording();
            return;   // exit early — the next tick will be cancelled when phase changes
          }
        } else {
          if (silenceMsRef.current > 0) silenceMsRef.current = 0;
          setAutoStopIn(null);
        }
      } else {
        silenceMsRef.current = 0;
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Encouragement messages during recording ───────────────────────
  // Surfaces at ENCOURAGEMENT_BY_SECOND keys. Each shows for 2.8s.
  useEffect(() => {
    if (phase !== 'recording') { setEncouragement(null); return; }
    const msg = ENCOURAGEMENT_BY_SECOND[recordSeconds];
    if (msg) {
      setEncouragement(msg);
      const id = setTimeout(() => setEncouragement(null), 2800);
      return () => clearTimeout(id);
    }
  }, [phase, recordSeconds]);

  // ── 5-second countdown ───────────────────────────────────────────
  const startCountdown = useCallback(() => {
    setPhase('countdown');
    setCountdown(5);
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(id);
          startRecording();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Begin recording ──────────────────────────────────────────────
  const startRecording = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (!stream) { setError('Camera not ready.'); setPhase('error'); return; }

    const mime = pickMime();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
    } catch (e) {
      setError('Your browser does not support video recording. Please try Chrome, Edge, or Safari on a newer device.');
      setPhase('error');
      return;
    }
    recorderRef.current = recorder;
    chunksRef.current = [];
    maxAudioRef.current = 0;
    isUploadRef.current = false;
    srPointerRef.current = 0;
    srTranscriptRef.current = '';
    setSpokenPromptIdx(null);
    setPracticeCheck('idle');
    setEvalResult(null);
    setPracticeTip('');
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' });
      blobRef.current = blob;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPhase('review');
    };
    recorder.start(250);
    startedAtRef.current = Date.now();
    setRecordSeconds(0);
    setPhase('recording');

    recordTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      setRecordSeconds(Math.floor(elapsed));
      if (elapsed >= maxDurationSeconds) stopRecording();
    }, 200);
  }, [maxDurationSeconds, previewUrl]);

  const stopRecording = useCallback(() => {
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setAutoStopIn(null);
  }, []);

  // ── Redo: discard the blob, return to setup phase ─────────────────
  const redo = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    blobRef.current = null;
    setTakeNumber((n) => n + 1);
    setRecordSeconds(0);
    setPhase('setup');
  }, [previewUrl]);

  // ── Practice readiness gate ───────────────────────────────────────
  // Grabs a few frames from the just-recorded blob (no upload), sends them to
  // our AI evaluator along with the peak audio level, and only lets the
  // customer continue when they're clearly visible, well framed/lit, and
  // audible. On a low score we surface a warm, specific tip and let them retry.
  // After a few tries we let them through anyway so no one gets stuck on a
  // warm-up. MAX_GRABS frames sampled across the clip.
  const PRACTICE_PASS_AFTER = 3;   // attempts before we offer a gentle override
  const grabFrames = useCallback(async () => {
    const blob = blobRef.current;
    if (!blob) return [];
    const url = URL.createObjectURL(blob);
    try {
      const v = document.createElement('video');
      v.muted = true; v.playsInline = true; v.preload = 'auto'; v.src = url;
      await new Promise((res, rej) => {
        v.onloadeddata = res; v.onerror = () => rej(new Error('frame video load failed'));
        setTimeout(res, 2500); // safety: don't hang the gate on a flaky decode
      });
      const dur = isFinite(v.duration) && v.duration > 0 ? v.duration : Math.max(2, recordSeconds || 4);
      const w = v.videoWidth || 640, h = v.videoHeight || 480;
      const canvas = document.createElement('canvas');
      // Downscale long edge to ~480px to keep the payload small + fast.
      const scale = Math.min(1, 480 / Math.max(w, h));
      canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      const times = [0.25, 0.5, 0.75].map((p) => Math.max(0.1, dur * p));
      const out = [];
      for (const t of times) {
        await new Promise((res) => {
          const onSeek = () => { v.removeEventListener('seeked', onSeek); res(); };
          v.addEventListener('seeked', onSeek);
          try { v.currentTime = t; } catch { res(); }
          setTimeout(res, 1200);
        });
        try {
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          out.push(canvas.toDataURL('image/jpeg', 0.7).split(',')[1]);
        } catch { /* tainted/again — skip this frame */ }
      }
      return out.filter(Boolean);
    } finally {
      URL.revokeObjectURL(url);
    }
  }, [recordSeconds]);

  // Runs automatically when a take lands in review. Grabs a few frames, asks
  // the evaluator to grade lighting, sound and framing, and stores the full
  // result so the review screen can show what is good and what to improve.
  const runEvaluation = useCallback(async () => {
    setPracticeCheck('checking');
    setPracticeTip('');
    setEvalResult(null);
    let result = null;
    try {
      const frames = await grabFrames();
      if (frames.length) {
        result = await api('/api/practice/evaluate', {
          method: 'POST',
          body: { invite_token, frames, audio_level: maxAudioRef.current, duration_seconds: recordSeconds },
        });
      }
    } catch (e) {
      console.warn('evaluation failed, treating as a pass', e?.message);
    }
    // Evaluator unreachable: never trap the customer. Treat as a clean pass.
    if (!result) {
      setEvalResult({ ready: true, lighting_ok: true, audio_ok: true, well_framed: true, person_visible: true, tip: '' });
      setPracticeCheck('ready');
      return;
    }
    setEvalResult(result);
    setPracticeTip(result.tip || '');
    setPracticeCheck(result.ready ? 'ready' : 'retry');
  }, [grabFrames, invite_token, recordSeconds]);

  // Auto-run the evaluation the moment a take enters review.
  useEffect(() => {
    if (phase === 'review' && practiceCheck === 'idle') runEvaluation();
  }, [phase, practiceCheck, runEvaluation]);

  // ── Accept the take: upload to Stream, then /accept ───────────────
  // Two visible stages:
  //   1. sending      — real % from XHR onprogress
  //   2. processing   — /accept call in flight (no useful progress info,
  //                     so we just show "Just a moment…")
  //   3. ready        — brief beat before phase flips to review
// Upload a video instead of recording. We accept only browser-playable video
  // so the customer can review it and so Stream reliably ingests it.
  const MAX_UPLOAD_MB = 500;
  const ALLOWED_VIDEO_EXT = ['mp4', 'm4v', 'mov', 'webm', 'ogv'];
  const isLikelyVideoFile = (file) => {
    if (file.type && file.type.startsWith('video/')) return true;
    const ext = (file.name || '').split('.').pop().toLowerCase();
    return ALLOWED_VIDEO_EXT.includes(ext);
  };
  // Confirm the browser can actually decode it (has a video track + real
  // duration). This is our "supported file" guarantee and gives us the length.
  const probeVideo = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    let done = false;
    const finish = (ok, duration) => {
      if (done) return; done = true;
      URL.revokeObjectURL(url);
      resolve({ ok, duration });
    };
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => finish(v.videoWidth > 0 && isFinite(v.duration) && v.duration > 0, v.duration);
    v.onerror = () => finish(false, 0);
    setTimeout(() => finish(false, 0), 8000);
    v.src = url;
  });

  const onPickFile = useCallback(() => {
    setUploadError('');
    fileInputRef.current?.click();
  }, []);

  const handleUploadFile = useCallback(async (file) => {
    setUploadError('');
    if (!file) return;
    if (!isLikelyVideoFile(file)) {
      setUploadError('Please choose a video file (MP4, MOV, or WebM).');
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setUploadError(`That video is over ${MAX_UPLOAD_MB} MB. Please choose a smaller file.`);
      return;
    }
    const probe = await probeVideo(file);
    if (!probe.ok) {
      setUploadError("We couldn't read that video. Please try an MP4 or MOV, for example one recorded on your phone.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    blobRef.current = file;                 // a File is a Blob; rides the same upload path
    isUploadRef.current = true;
    setRecordSeconds(Math.round(probe.duration || 0));
    setSpokenPromptIdx(null);
    setPreviewUrl(URL.createObjectURL(file));
    setTakeNumber((n) => n + 1);
    setPhase('review');
  }, [previewUrl]);

    const acceptTake = useCallback(async () => {
    if (!blobRef.current) return;
    setPhase('uploading');
    setUploadPct(0);
    setUploadStage('sending');
    setError(null);

    try {
      let clip_id = null, upload_url = null;
      if (getUploadUrl) {
        ({ upload_url } = await getUploadUrl());
      } else {
        ({ clip_id, upload_url } = await api('/api/clip/upload-url', {
          method: 'POST',
          body: {
            invite_token,
            script_key: script.key,
            ...(questionId ? { question_id: questionId } : {}),
            filled_script: filledScript,
            is_upload: isUploadRef.current,
            // Uploaded files can be longer than a recorded take; give Stream enough
            // headroom (capped server-side at 10 min).
            max_duration_seconds: isUploadRef.current
              ? Math.min(600, (recordSeconds || 0) + 15)
              : maxDurationSeconds + 5,
          },
        }));
      }

      const fd = new FormData();
      const upName = (blobRef.current && blobRef.current.name) ? blobRef.current.name : `clip_${script.key}.webm`;
      fd.append('file', blobRef.current, upName);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', upload_url, true);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadPct(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('upload network error'));
        xhr.send(fd);
      });

      // Upload complete — XHR finished. Flip to 'processing' while /accept runs.
      setUploadPct(100);
      setUploadStage('processing');

      const duration = blobRef.current.size ? recordSeconds || null : null;
      if (getUploadUrl) {
        // Custom target (e.g. interviewer question take): no clip row, no accept.
        setUploadStage('ready');
        setTimeout(() => onAccepted?.({ duration_seconds: duration }), 700);
      } else {
        const clip = await api(`/api/clip/${clip_id}/accept`, {
          method: 'POST',
          body: { invite_token, filled_script: filledScript, duration_seconds: duration },
        });

        // /accept resolved. Brief "Ready!" beat, then leave the screen.
        setUploadStage('ready');
        setTimeout(() => onAccepted?.({ ...clip, clip_id }), 700);
      }
    } catch (e) {
      console.error('upload/accept failed', e);
      setError("We couldn't save that take. Check your internet connection and try again.");
      setPhase('review');
    }
  }, [invite_token, script.key, filledScript, maxDurationSeconds, recordSeconds, onAccepted, getUploadUrl]);
  acceptTakeRef.current = acceptTake;
  // Keep this take: real -> upload+accept, practice -> just continue.
  const passAction = useCallback(() => { if (practice) onAccepted?.(); else acceptTakeRef.current?.(); }, [practice, onAccepted]);

  // ── Derived display state ────────────────────────────────────────
  const mm = String(Math.floor(recordSeconds / 60)).padStart(2, '0');
  const ss = String(recordSeconds % 60).padStart(2, '0');
  const maxMm = String(Math.floor(maxDurationSeconds / 60)).padStart(2, '0');
  const maxSs = String(maxDurationSeconds % 60).padStart(2, '0');

  const canLeave = phase !== 'recording' && phase !== 'uploading' && phase !== 'countdown';

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="vt-rec-page">
      <header className="vt-rec-top">
        <div className="vt-rec-left">
          <img
            className="vt-rec-logo"
            src={brandLogo(brand)}
            alt="Brand logo"
          />
        </div>
        <div className="vt-rec-title">
          <span className="vt-rec-clip-of">{practice ? 'Practice run · just for you' : interview ? `Answer ${clipNumber} of ${totalClips}` : freeform ? 'In your own words' : `Clip ${clipNumber} of ${totalClips}`}</span>
          {script?.title || 'Recording'}
        </div>
        <div className="vt-rec-right">
          <button
            type="button"
            className="vt-rec-close"
            onClick={onCancel}
            disabled={!canLeave}
            aria-label="Back to script"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
            Back
          </button>
        </div>
      </header>

      {/* ── Permission preflight ─────────────────────────────────── */}
      {phase === 'permission' && (
        <div className="vt-rec-preflight">
          <div className="vt-preflight-card animate-fade-in-up">
            <div className="vt-preflight-icons" aria-hidden="true">
              <span className="vt-pf-icon vt-pf-cam">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="13" height="12" rx="2"/>
                  <path d="M16 10l5-3v10l-5-3z"/>
                </svg>
              </span>
              <span className="vt-pf-plus">+</span>
              <span className="vt-pf-icon vt-pf-mic">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="3" width="6" height="12" rx="3"/>
                  <path d="M5 11a7 7 0 0 0 14 0"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                </svg>
              </span>
            </div>
            <h2 className="vt-preflight-title">Let's get your camera ready.</h2>
            <p className="vt-preflight-sub">
              We need your camera and microphone to record. Your browser will ask first.
              Tap <strong>Allow</strong> when it does.
            </p>

            {/* Boomer-friendly recording tips. Added after we got a
                recording where the customer read directly off the
                screen — her bifocals reflected the teleprompter text
                back at the camera. The tips here target the three
                things that go wrong most often with this demographic:
                1) reading instead of looking at the camera (which the
                   reflection bug is a symptom of),
                2) backlit window framing (silhouette / harsh light),
                3) trying to be too perfect (over-rehearsed feel).
                The glasses tip is its own line because it's specific
                and not obvious to non-glasses-wearers reading copy.
                Tone is reassuring + concrete — older customers respond
                better to "do this" than to "consider whether..." */}
            <div className="vt-preflight-tips">
              <div className="vt-preflight-tips-head">
                <span className="vt-pf-tips-pill">3 quick tips</span>
                <span className="vt-pf-tips-sub">It takes 30 seconds to set up. Worth it!</span>
              </div>
              <ul className="vt-preflight-tips-list">
                <li>
                  <span className="vt-pf-tip-ico" aria-hidden="true">👀</span>
                  <div>
                    <strong>Look at the camera, not the words.</strong>
                    <span>Read a line, then look up and say it like you're talking to a friend. You don't have to get every word exactly right.</span>
                  </div>
                </li>
                <li>
                  <span className="vt-pf-tip-ico" aria-hidden="true">💡</span>
                  <div>
                    <strong>Face a window or a lamp.</strong>
                    <span>The light should be in front of you. If a window's behind you, turn around so it's facing you instead.</span>
                  </div>
                </li>
                <li>
                  <span className="vt-pf-tip-ico" aria-hidden="true">👓</span>
                  <div>
                    <strong>If you wear glasses, tilt the screen down a bit.</strong>
                    <span>That way the screen doesn't reflect in your lenses.</span>
                  </div>
                </li>
              </ul>
            </div>

            {(permState === 'denied' || error) && (
              <div className="vt-preflight-error" role="alert">
                {error || 'Your browser blocked the camera. Check your browser settings and try again.'}
              </div>
            )}
            <button
              className="vt-btn vt-btn-primary vt-btn-lg vt-tap vt-preflight-cta"
              onClick={requestPermission}
              disabled={permState === 'asking'}
            >
              {permState === 'asking' ? 'Waiting for you…'
                : permState === 'denied' ? 'Try again'
                : 'Allow camera & microphone →'}
            </button>
            <p className="vt-preflight-tip">
              We only use it for this recording. Nothing is sent until you press Submit.
            </p>
          </div>
        </div>
      )}

      {/* ── Everything else: camera stage + side panel ───────────── */}
      {phase !== 'permission' && (
        <div className="vt-rec-body">
          {/* Camera stage */}
          <div className={'vt-rec-stage vt-phase-' + phase} ref={stageRef}>
            {/* Live preview — mirrored selfie-style */}
            {(phase === 'setup' || phase === 'countdown' || phase === 'recording' || phase === 'uploading') && (
              <video
                ref={liveVideoRef}
                playsInline muted autoPlay
                style={{ transform: 'scaleX(-1)' }}
              />
            )}

            {/* Head-position guide — shows where the face should sit and how far
                from the camera. Bright while setting up, fades once recording so
                it doesn't distract. SVG scales with the stage via viewBox. */}
            {(phase === 'setup' || phase === 'countdown' || phase === 'recording') && (
              <div className={'vt-rec-guide vt-rec-guide-' + phase + (faceState ? ' vt-rec-guide-' + faceState : '')} aria-hidden="true">
                <div className="vt-rec-guide-head" />
                {/* Hint: adaptive once the detector is live, else the static tip.
                    Shown during setup/countdown always; during recording only
                    if positioning is off, so a good frame stays distraction-free. */}
                {(phase === 'setup' || phase === 'countdown' || (phase === 'recording' && faceState && faceState !== 'good')) && (
                  <div className="vt-rec-guide-hint">
                    {faceState ? faceHint : "Sit back so your head and shoulders fill the oval"}
                  </div>
                )}
              </div>
            )}

            {/* Playback — not mirrored, this is what the audience sees */}
            {phase === 'review' && previewUrl && (
              <video
                ref={reviewVideoRef}
                playsInline controls
                src={previewUrl}
              />
            )}

            {phase === 'error' && (
              <div className="vt-rec-cam-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <div className="vt-rec-cam-placeholder-title">Camera unavailable</div>
                <div className="vt-rec-cam-placeholder-body">Tap "Try again" below to grant permission.</div>
              </div>
            )}

            {/* Status pill + timer overlay (top row) */}
            {(phase === 'setup' || phase === 'countdown' || phase === 'recording' || phase === 'uploading' || phase === 'review') && (
              <div className="vt-rec-overlay">
                <div className="vt-rec-top-row">
                  {phase === 'setup' && (
                    <span className="vt-rec-status ready"><span className="vt-rec-status-dot" />Camera ready</span>
                  )}
                  {phase === 'countdown' && (
                    <span className="vt-rec-status review"><span className="vt-rec-status-dot" />Get comfortable</span>
                  )}
                  {phase === 'recording' && (
                    <>
                      <span className="vt-rec-status"><span className="vt-rec-status-dot" />RECORDING</span>
                      <span className="vt-rec-timer">{mm}:{ss} / {maxMm}:{maxSs}</span>
                    </>
                  )}
                  {phase === 'uploading' && (
                    <span className="vt-rec-status review"><span className="vt-rec-status-dot" />Saving your video</span>
                  )}
                  {phase === 'review' && (
                    <span className="vt-rec-status review">
                      <span className="vt-rec-status-dot" />How did it go?
                      {takeNumber > 1 && ` · TAKE ${takeNumber}`}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Countdown overlay — big digit + per-second tip */}
            {phase === 'countdown' && (
              <div className="vt-countdown-overlay">
                <div className="vt-countdown-num" key={countdown}>
                  {countdown > 0 ? countdown : 'Go!'}
                </div>
                <div className="vt-countdown-tip">
                  {COUNTDOWN_TIPS[countdown] || ' '}
                </div>
              </div>
            )}

            {/* In-stage running teleprompter — floats near the TOP of the camera
                (close to the lens) so the customer's eyes stay up while reading.
                Visible while recording on every device. */}
            {phase === 'recording' && (
              <div className="vt-rec-tele-overlay" style={{ ['--vt-tele-fs']: TELE_SIZES[teleSize] + 'px' }}>
                {renderRunningTeleprompter(script, filledScript, recordSeconds, maxDurationSeconds, spokenPromptIdx)}
              </div>
            )}

            {/* Encouragement toast — fades in/out */}
            {encouragement && (
              <div className="vt-encouragement" aria-live="polite">
                <span className="vt-enc-icon">♥</span>{encouragement}
              </div>
            )}

            {/* Auto-stop warning — appears when we're 3 seconds from cutoff */}
            {phase === 'recording' && autoStopIn !== null && (
              <div className="vt-auto-stop-toast" aria-live="polite">
                <span className="vt-rec-status-dot" />
                Quiet… stopping in {autoStopIn}s
              </div>
            )}

            {/* Audio bars — real mic data, visible during setup + recording */}
            {(phase === 'setup' || phase === 'recording') && (
              <div className="vt-audio-bars" aria-hidden="true">
                {bars.map((h, i) => (
                  <span
                    key={i}
                    className="vt-audio-bar"
                    style={{ height: `${Math.max(6, h * 100)}%` }}
                  />
                ))}
              </div>
            )}

            {/* Two-stage upload overlay */}
            {phase === 'uploading' && (
              <div className="vt-upload-overlay">
                <div className="vt-upload-card">
                  <div className="vt-upload-stages">
                    <div className={'vt-us-row' + (uploadStage !== 'sending' ? ' done' : ' active')}>
                      <span className="vt-us-dot">
                        {uploadStage !== 'sending' ? '✓' : '1'}
                      </span>
                      <div className="vt-us-text">
                        <div className="vt-us-title">Sending your video</div>
                        <div className="vt-us-sub">
                          {uploadStage === 'sending' ? `${uploadPct}% sent` : 'Sent safely'}
                        </div>
                      </div>
                    </div>
                    <div className={'vt-us-row' + (uploadStage === 'ready' ? ' done' : uploadStage === 'processing' ? ' active' : '')}>
                      <span className="vt-us-dot">
                        {uploadStage === 'ready' ? '✓'
                          : uploadStage === 'processing' ? <span className="vt-spinny" />
                          : '2'}
                      </span>
                      <div className="vt-us-text">
                        <div className="vt-us-title">Getting it ready to watch</div>
                        <div className="vt-us-sub">
                          {uploadStage === 'processing' ? 'Just a moment…'
                            : uploadStage === 'ready' ? 'Ready!'
                            : 'Coming up next'}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="vt-upload-bar">
                    <span style={{ width: `${uploadStage === 'ready' || uploadStage === 'processing' ? 100 : uploadPct}%` }} />
                  </div>
                  <p className="vt-upload-tip">
                    Hang tight. We're saving this safely. Nothing is shared with anyone yet.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Side panel: teleprompter + phase-specific CTAs */}
          <div className="vt-rec-side">
            <div className="vt-tele-panel">
              <div className="vt-tele-panel-head">
                <h3>
                  {phase === 'setup'     && (practice ? 'Just read this friendly line' : interview ? 'Answer in your own words' : freeform ? 'Say it in your own words' : 'Practice in your head first')}
                  {phase === 'countdown' && ((freeform || interview) ? 'Get ready to talk' : 'Get ready to read')}
                  {phase === 'recording' && ((freeform || interview) ? 'Just talk to the camera' : 'Read this out loud')}
                  {phase === 'uploading' && 'Saving your video'}
                  {phase === 'review'    && (practice ? "How did that look and sound?" : "Here's what you said")}
                  {phase === 'error'     && 'Something went wrong'}
                </h3>
                <div className="vt-tele-size-toggle" role="group" aria-label="Teleprompter text size">
                  {['sm', 'md', 'lg'].map((sz) => (
                    <button
                      key={sz}
                      type="button"
                      className={teleSize === sz ? 'active' : ''}
                      onClick={() => setTeleSize(sz)}
                      aria-pressed={teleSize === sz}
                      aria-label={sz === 'sm' ? 'Small text' : sz === 'md' ? 'Medium text' : 'Large text'}
                    >
                      <span className={'vt-s' + (sz === 'sm' ? '1' : sz === 'md' ? '2' : '3')}>A</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tiny contextual reminder during setup. Same message as the
                  preflight tips, but positioned where the customer is
                  literally about to make the choice (read off screen vs
                  look at camera). On setup phase only — once recording
                  starts we get out of the way. */}
              {phase === 'setup' && (
                <div className="vt-tele-cue" role="note">
                  <span className="vt-tele-cue-ico" aria-hidden="true">{practice ? '😊' : interview ? '🎤' : freeform ? '💬' : '👀'}</span>
                  <span>
                    {practice
                      ? <>No pressure. This one's just practice. Read it your way, then watch yourself back.</>
                      : interview
                      ? <>You just watched the question. <strong>Look at the camera</strong> and answer like you're telling a friend. A sentence or two is plenty.</>
                      : freeform
                      ? <>No script here. Just <strong>look at the camera</strong> and say what your experience has been like, in your own words. A sentence or two is plenty.</>
                      : <>Glance at the words, then <strong>look at the camera</strong> and say it.{' '}
                          Don't worry about reading it word-for-word.</>}
                  </span>
                </div>
              )}

              <div className="vt-tele" style={{ '--vt-tele-fs': TELE_SIZES[teleSize] + 'px' }}>
                {interview
                  ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <p style={{ margin: 0, opacity: 0.6, fontSize: '0.82em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>The question</p>
                      <p style={{ margin: 0, fontWeight: 700 }}>{questionText || script.title}</p>
                      {questionHint && (
                        <p style={{ margin: 0, opacity: 0.75, lineHeight: 1.6 }}>{questionHint}</p>
                      )}
                      <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9em' }}>Answer in your own words. There's no wrong answer.</p>
                    </div>
                  )
                  : freeform
                  ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <p style={{ margin: 0, fontWeight: 700 }}>Talk about your experience in your own words.</p>
                      <ul style={{ margin: 0, paddingLeft: 18, opacity: 0.85, lineHeight: 1.7 }}>
                        <li>What made you switch or stay?</li>
                        <li>What do you like most?</li>
                        <li>Would you recommend it, and why?</li>
                      </ul>
                      <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9em' }}>Pick one, or just speak from the heart. There's no wrong answer.</p>
                    </div>
                  )
                  : phase === 'recording'
                  ? (
                    <div className="vt-tele-recording-cue">
                      <span className="vt-tele-cue-arrow" aria-hidden="true">↑</span>
                      Read along from the words on your video, up near the camera. Look at the camera, not down here.
                    </div>
                  )
                  : renderTeleprompterJSX(script, filledScript)}
              </div>
            </div>

            {/* Phase-specific CTA stack */}
            <div className="vt-rec-cta-stack">
              {phase === 'setup' && (
                <>
                  {networkSlow && (
                    <div className="vt-net-warning" role="alert">
                      <span className="vt-net-warning-ico">📶</span>
                      <div className="vt-net-warning-text">
                        <strong>Your connection looks slow.</strong>
                        <span>Try Wi-Fi if you can. Your video may take a little longer to save.</span>
                      </div>
                    </div>
                  )}
                  <button className="vt-rec-start-pill vt-tap" onClick={startCountdown} aria-label="Start recording">
                    <span className="vt-rec-start-icon"><span className="vt-rec-start-icon-dot" /></span>
                    <span className="vt-rec-start-text">
                      <span className="vt-rec-start-title">Tap to start recording</span>
                      <span className="vt-rec-start-sub">{(freeform || interview) ? '5-second countdown, then just talk' : '5-second countdown, then read aloud'}</span>
                    </span>
                  </button>

                  {!practice && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/ogg,.mp4,.m4v,.mov,.webm,.ogv"
                        style={{ display: 'none' }}
                        onChange={(e) => { const fl = e.target.files && e.target.files[0]; e.target.value = ''; if (fl) handleUploadFile(fl); }}
                      />
                      <button type="button" className="vt-rec-upload-btn vt-tap" onClick={onPickFile}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Upload a video instead
                      </button>
                      {uploadError && <div className="vt-rec-upload-error" role="alert">{uploadError}</div>}
                    </>
                  )}

                  {practice && onSkip && (
                    <button type="button" className="vt-link-button vt-tap vt-practice-skip" onClick={onSkip}>
                      Skip practice, go to my stories
                    </button>
                  )}

                  <div className="vt-setup-tips">
                    <span className="vt-setup-tip">
                      <span className="vt-tip-ico" style={{ ['--vt-ico']: 'var(--ravepilot-orange)' }}>☼</span>
                      <span>Face a window so your face is lit</span>
                    </span>
                    <span className="vt-setup-tip">
                      <span className="vt-tip-ico" style={{ ['--vt-ico']: '#1A7A4D' }}>♪</span>
                      <span>Quiet room. We can hear you well.</span>
                    </span>
                    <span className="vt-setup-tip">
                      <span className="vt-tip-ico" style={{ ['--vt-ico']: '#D43A6F' }}>♥</span>
                      <span>Re-record as many times as you like</span>
                    </span>
                  </div>
                </>
              )}

              {phase === 'countdown' && (
                <>
                  <div className="vt-rec-busy-pill">
                    Camera starts in {countdown > 0 ? countdown : 0}…
                  </div>
                  <div className="vt-rec-hint">You can always stop and start over.</div>
                </>
              )}

              {phase === 'recording' && (
                <>
                  <button className="vt-rec-stop-pill vt-tap" onClick={stopRecording}>
                    <span className="vt-stop-square" />
                    I'm done. Stop recording.
                  </button>
                  <div className="vt-rec-hint">Take your time. You can pause whenever.</div>
                </>
              )}

              {phase === 'uploading' && (
                <>
                  <div className="vt-rec-busy-pill">
                    {uploadStage === 'sending'    ? 'Uploading your video…'
                      : uploadStage === 'processing' ? `${brandName(brand)} is polishing your video…`
                      : 'Ready!'}
                  </div>
                  <div className="vt-rec-hint">Hang tight — we're captioning and polishing this into an ad. Don't close the page yet.</div>
                </>
              )}

              {phase === 'review' && (
                <>
                  {(practiceCheck === 'idle' || practiceCheck === 'checking') ? (
                    <div className="vt-eval-checking">
                      <span className="vt-submit-spinner" aria-hidden="true" />
                      <div>
                        <div className="vt-eval-checking-title">Checking your video…</div>
                        <div className="vt-eval-checking-sub">Looking at your lighting, sound and framing.</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="vt-eval-title">
                        {evalResult?.ready ? (practice ? 'Looking good!' : "Nice take! Here's how it looks.") : 'Want an even better take?'}
                      </div>
                      <div className="vt-eval-rows">
                        <div className={'vt-eval-row ' + (evalResult?.lighting_ok ? 'ok' : 'warn')}>
                          <span className="vt-eval-ico" aria-hidden="true">{evalResult?.lighting_ok ? '✓' : '!'}</span>
                          <div>
                            <div className="vt-eval-row-label">Lighting</div>
                            <div className="vt-eval-row-text">{evalResult?.lighting_ok ? 'Nice and bright, we can see you well.' : 'Face a window so the light is on your face, not behind you.'}</div>
                          </div>
                        </div>
                        <div className={'vt-eval-row ' + (evalResult?.audio_ok ? 'ok' : 'warn')}>
                          <span className="vt-eval-ico" aria-hidden="true">{evalResult?.audio_ok ? '✓' : '!'}</span>
                          <div>
                            <div className="vt-eval-row-label">Sound</div>
                            <div className="vt-eval-row-text">{evalResult?.audio_ok ? 'Clear and easy to hear.' : 'Speak up a little so we catch every word.'}</div>
                          </div>
                        </div>
                        <div className={'vt-eval-row ' + (evalResult?.well_framed ? 'ok' : 'warn')}>
                          <span className="vt-eval-ico" aria-hidden="true">{evalResult?.well_framed ? '✓' : '!'}</span>
                          <div>
                            <div className="vt-eval-row-label">Framing</div>
                            <div className="vt-eval-row-text">{evalResult?.well_framed ? 'Head and shoulders, nicely centered.' : 'Scoot back so we can see your head and shoulders.'}</div>
                          </div>
                        </div>
                      </div>

                      {practiceTip && !evalResult?.ready && (
                        <div className="vt-eval-tip"><span aria-hidden="true">💡</span><span>{practiceTip}</span></div>
                      )}

                      <div className="vt-rec-action-row">
                        <button className="vt-btn vt-btn-secondary vt-tap" onClick={redo}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                            <path d="M3 12a9 9 0 1 0 3-6.7" />
                            <polyline points="3 4 3 9 8 9" />
                          </svg>
                          Re-record
                        </button>
                        {(practice || evalResult?.ready) && (
                          <button className="vt-btn vt-btn-primary vt-btn-lg vt-tap" onClick={passAction}>
                            {practice ? 'Continue →' : 'Use this take →'}
                          </button>
                        )}
                      </div>

                      <div className="vt-rec-hint">
                        {evalResult?.ready
                          ? (practice ? "This was just practice, so we won't keep it." : 'Happy with it? Use it, or re-record as many times as you like.')
                          : (practice ? "This was just practice. Give it another go when you're ready." : 'A quick re-record with the tip above will make your video shine.')}
                      </div>
                    </>
                  )}
                </>
              )}

              {phase === 'error' && (
                <>
                  <div className="vt-rec-error" role="alert">{error}</div>
                  <div className="vt-rec-action-row">
                    <button
                      className="vt-btn vt-btn-primary vt-btn-lg vt-tap"
                      onClick={() => { setError(null); setPhase('permission'); }}
                    >
                      Try again
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Teleprompter rendering — JSX so we can color the filled blanks orange
// to distinguish them from the script's static text at a glance.
// ─────────────────────────────────────────────────────────────────────────

function renderTeleprompterJSX(script, fills) {
  if (!script || !script.prompts) return null;
  const segs = [];
  script.prompts.forEach((p, pi) => {
    if (pi > 0) {
      segs.push(<br key={`br${pi}a`} />);
      segs.push(<br key={`br${pi}b`} />);
    }
    const parts = p.template.split('___');
    parts.forEach((t, i) => {
      if (t) segs.push(<span key={`${pi}t${i}`}>{t}</span>);
      if (i < parts.length - 1) {
        const v = ((fills || {})[`${pi}.${i}`] || '').trim();
        if (v) {
          segs.push(<strong key={`${pi}b${i}`} style={{ color: 'var(--ravepilot-orange)', fontWeight: 500 }}>{v}</strong>);
        } else {
          segs.push(<span key={`${pi}b${i}`} style={{ color: '#9CA3AF' }}>______</span>);
        }
      }
    });
  });
  return <>{segs}</>;
}

// In-stage running teleprompter — surfaces the prompt the speaker should
// be reading right now, derived from elapsed/target ratio.
function renderPromptLine(p, fills, promptIdx) {
  const parts = p.template.split('___');
  return parts.map((t, i) => (
    <span key={i}>
      {t}
      {i < parts.length - 1 && (
        <strong style={{ color: 'var(--ravepilot-orange)', fontWeight: 500 }}>
          {((fills || {})[`${promptIdx}.${i}`] || '____').trim() || '____'}
        </strong>
      )}
    </span>
  ));
}

// Running teleprompter shown on the camera while recording. If `spokenIdx` is a
// number (speech-follow active) it drives the current line; otherwise we fall
// back to advancing by elapsed time. We also show the NEXT line dimmed beneath
// so it reads like a scrolling teleprompter.
// Advance the running teleprompter at a natural reading pace when live
// speech-follow is unavailable (e.g. on iOS). Each prompt lingers roughly as
// long as it takes to read aloud, so every line is shown in turn rather than
// being stretched across the whole max recording length (which left later
// lines hidden until ~40s in).
function teleReadingIdx(prompts, elapsed) {
  const WPS = 2.5;        // ~150 wpm — a brisk but readable on-camera pace
  const MIN_LINE = 2.0;   // never flip a line faster than this
  let acc = 0;
  for (let i = 0; i < prompts.length; i++) {
    const text = String((prompts[i] && prompts[i].template) || '').replace(/___/g, 'word');
    const words = text.trim().split(/\s+/).filter(Boolean).length || 1;
    const dur = Math.max(MIN_LINE, words / WPS);
    if (elapsed < acc + dur) return i;
    acc += dur;
  }
  return prompts.length - 1;
}

function renderRunningTeleprompter(script, fills, elapsed, target, spokenIdx) {
  if (!script || !script.prompts || script.prompts.length === 0) return null;
  let promptIdx;
  if (typeof spokenIdx === 'number') {
    promptIdx = Math.min(script.prompts.length - 1, Math.max(0, spokenIdx));
  } else {
    promptIdx = teleReadingIdx(script.prompts, elapsed);
  }
  const p = script.prompts[promptIdx];
  if (!p) return null;
  const next = script.prompts[promptIdx + 1] || null;
  return (
    <>
      <div className="vt-tele-line-current">{renderPromptLine(p, fills, promptIdx)}</div>
      {next && (
        <div className="vt-tele-line-next">{renderPromptLine(next, fills, promptIdx + 1)}</div>
      )}
    </>
  );
}
